import React, { useEffect, useRef } from "react";
import { Avatar, Box, Chip, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ManageAccounts as ManageAccountsIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { useClinicTheme } from "../../theme";
import { COLORS } from "../../styles/uiConstants";
import { getRoleLabel } from "../../utils/roleLabels";
import EmptyState from "../../components/atoms/EmptyState";
import ClinicLabel from "../../components/atoms/ClinicLabel";
import {
  ListView,
  ListSkeleton,
  ListPagination,
} from "../../components/organisms/list";
import useAutoPageSize from "../../hooks/useAutoPageSize";
import { getStatusChipSx, getRoleChipSx } from "./constants";
import UserCard from "./UserCard";
import { formatPhoneDisplay } from "../../components/atoms/PhoneInput";

/** Başlıklar literal büyük harf — CSS uppercase Türkçe İ'yi bozar. */
export const USER_COLUMNS = [
  { key: "user", label: "KULLANICI" },
  { key: "accountStatus", label: "DURUM" },
  { key: "clinic", label: "KLİNİK" },
  { key: "contact", label: "İLETİŞİM" },
  { key: "actions", label: "" },
];

export default function UsersList({
  users = [],
  isLoading = false,
  page,
  pageSize,
  totalItems,
  totalPages,
  onPageChange,
  onPageSizeChange,
  onOpenDetail,
  onMenuOpen,
  onClinicClick,
  canUseActions = false,
}) {
  const { palette } = useClinicTheme();
  const bodyRef = useRef(null);
  const autoPageSize = useAutoPageSize(bodyRef, { rowHeight: 56, headerHeight: 40 });

  // Ölçülen satır sayısını yukarı bildir — sayfalama matematiği useUserManagement'te.
  useEffect(() => {
    onPageSizeChange?.(autoPageSize);
  }, [autoPageSize, onPageSizeChange]);

  const columns = [
    {
      key: "user",
      label: "KULLANICI",
      render: (user) => (
        <Box
          component="button"
          type="button"
          onClick={() => onOpenDetail?.(user)}
          aria-label={`${user?.name} ${user?.surname} detayını aç`}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            bgcolor: "transparent",
            border: "none",
            padding: 0,
            margin: 0,
            cursor: "pointer",
            textAlign: "left",
            font: "inherit",
            "&:hover .userName": {
              textDecoration: "underline",
              color: palette.primary,
            },
            "&:focus-visible": {
              outline: `2px solid ${alpha(palette.primary, 0.5)}`,
              outlineOffset: 2,
            },
          }}
        >
          <Avatar
            variant="rounded"
            sx={{
              width: 32,
              height: 32,
              fontSize: "0.8rem",
              fontWeight: 700,
              bgcolor: alpha(palette.primary, 0.12),
              color: palette.primary,
            }}
          >
            {(user?.name || "?")[0].toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              className="userName"
              sx={{ fontWeight: 600, fontSize: "0.8125rem", color: COLORS.slate800 }}
              noWrap
            >
              {user?.name} {user?.surname}
            </Typography>
            <Chip
              size="small"
              label={getRoleLabel(user?.role)}
              sx={{ ...getRoleChipSx(user?.role), height: 20, fontSize: "0.625rem", mt: 0.25, borderRadius: "999px" }}
            />
          </Box>
        </Box>
      ),
    },
    {
      key: "accountStatus",
      label: "DURUM",
      cellSx: { width: 140 },
      render: (user) => (
        <Chip
          size="small"
          label={user?.accountStatus}
          sx={{ ...getStatusChipSx(user?.accountStatus), height: 24, fontSize: "0.6875rem", borderRadius: "999px" }}
        />
      ),
    },
    {
      key: "clinic",
      label: "KLİNİK",
      cellSx: { width: 200 },
      render: (user) => (
        <ClinicLabel
          clinic={user?.clinic}
          onClick={user?.clinic?.id ? () => onClinicClick?.(user.clinic.id) : undefined}
        />
      ),
    },
    {
      key: "contact",
      label: "İLETİŞİM",
      render: (user) => (
        <Box>
          <Typography sx={{ fontSize: "0.8125rem", color: COLORS.slate700 }} noWrap>
            {user?.email}
          </Typography>
          <Typography sx={{ fontSize: "0.6875rem", color: COLORS.slate400 }} noWrap>
            {formatPhoneDisplay(user?.phone)}
          </Typography>
        </Box>
      ),
    },
    {
      key: "actions",
      label: "",
      align: "center",
      cellSx: { width: 56 },
      render: (user) =>
        canUseActions ? (
          <IconButton
            size="small"
            aria-label="İşlemler"
            onClick={(e) => {
              e.stopPropagation();
              onMenuOpen?.(e, user);
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ) : null,
    },
  ];

  return (
    <ListView
      bodyRef={bodyRef}
      columns={columns}
      data={users}
      rowKey="id"
      rowSx={{
        "&:hover": { bgcolor: alpha(palette.primary, 0.05) },
      }}
      renderMobileCard={(user) => (
        <UserCard
          user={user}
          onOpenDetail={onOpenDetail}
          onMenuOpen={onMenuOpen}
          canUseActions={canUseActions}
        />
      )}
      isLoading={isLoading}
      loadingNode={
        <ListSkeleton columns={USER_COLUMNS} rowCount={pageSize} message="Kullanıcılar yükleniyor..." />
      }
      empty={
        <EmptyState
          icon={<ManageAccountsIcon />}
          title="Sonuç bulunamadı"
          description="Arama/filtre kriterlerinize uygun kullanıcı bulunamadı."
        />
      }
      footer={
        <ListPagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={pageSize}
          onPageChange={onPageChange}
        />
      }
    />
  );
}
